import {
	GridItem,
	Heading,
	Icon,
	FormControl,
	FormLabel,
	Input,
	Select,
	SimpleGrid,
	Slider,
	SliderTrack,
	Box,
	SliderFilledTrack,
	SliderThumb,
	Grid,
	Tooltip,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	NumberIncrementStepper,
	NumberDecrementStepper,
} from '@chakra-ui/react';
import { InfoIcon, ArrowLeftIcon, QuestionIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import {
	CartesianGrid,
	ResponsiveContainer,
	XAxis,
	YAxis,
	Tooltip as RechartsTooltip,
	Line,
	ReferenceArea,
	ComposedChart,
	Label
} from 'recharts';
import { format, parseJSON } from 'date-fns';
import { scaleTime } from 'd3-scale';

export const CalculateSavings = () => {

	const [totalKwh, setTotalkwh] = useState<number | undefined>();
	const [level, setLevel] = useState<number>(5);
	const [day, setDay] = useState<string>("");
	const [extraWaterHours, setExtraWaterHours] = useState<number>(1);

	return (
		<Grid
			templateAreas={`"form graph"
                  "form graph"`}
			gridTemplateColumns={'350px 1fr'}
			minH='700px'
			gap='1'
			color='blackAlpha.700'
			fontWeight='bold'
		>
			<GridItem key='form' pt='10' pl='10' pr='10' area={'form'}>
				<FormControl pb='10px'>
					<FormLabel>Din årsförbrukning <Tooltip label='Fyll i din elförbrukning för värme och varmvatten' fontSize='md'><Icon as={QuestionIcon} color="green.400" /></Tooltip></FormLabel>
					<Input placeholder='kWh' value={totalKwh ?? ''} onChange={(e) => setTotalkwh(e.target.valueAsNumber)} type='number' />
				</FormControl>
				<FormControl pb='10px'>
					<FormLabel htmlFor='level' >Tillåten prisnivå <Tooltip label='Dra här när du startat beräkningen för att blockera värmen baserat på elprisnivå' fontSize='md'><Icon as={QuestionIcon} color="green.400" /></Tooltip></FormLabel>
					<Slider
						onChange={(val) => setLevel(val)}
						id='level'
						value={level ?? ''}
						min={1} max={9} step={1}>
						<SliderTrack bg='teal.100'>
							<Box position='relative' right={10} />
							<SliderFilledTrack bg='teal.500' />
						</SliderTrack>
						<SliderThumb boxSize={6} />
					</Slider>
				</FormControl>
				<FormControl pb='10px'>
					<FormLabel>Extra varmvatten <Tooltip label='Hur många timmar per dyng vill du göra extra varmvatten. Då har du varmvatten förberett när elpriset stiger under dagen.' fontSize='md'><Icon as={QuestionIcon} color="green.400" /></Tooltip></FormLabel>
					<NumberInput defaultValue={1} min={0} max={24} onChange={(_, num) => setExtraWaterHours(num)}>
						<NumberInputField />
						<NumberInputStepper>
							<NumberIncrementStepper />
							<NumberDecrementStepper />
						</NumberInputStepper>
					</NumberInput>
				</FormControl>
				<FormControl>
					<FormLabel>Datum för elpriser</FormLabel>
					<Select onChange={(e) => setDay(e.target.value)} placeholder='Välj datum att basera uppskattningen på'>
						{Object.keys(graphSourceData).map((r: any) => (
							<option key={r} value={r}>{r}</option>
						))}
					</Select>
				</FormControl>
			</GridItem>
			<GridItem key='graph' pr='10' pt='10' area={'graph'}>
				<PriceGraph maxLevel={level} hotWaterHours={extraWaterHours} kwh={totalKwh} data={graphSourceData[day] ?? []} />
			</GridItem>
		</Grid>
	)

}


const PriceGraph = ({ maxLevel, hotWaterHours, kwh, data }: { maxLevel: number, hotWaterHours: number, kwh: number | undefined, data: Price[] }) => {
	if (data.length === 0) {
		return (
			<div>
				<Heading pt={'25px'} ><Icon as={ArrowLeftIcon} color="teal.300" mr={'8px'} />Fyll i din nuvarande årsförbrukning för värme och varmvatten!</Heading>
				<Heading pt={'195px'} ><Icon as={ArrowLeftIcon} color="teal.300" mr={'8px'} />Välj dag för att starta beräkningen!</Heading>
			</div>
		);
	}


	const sorted = structuredClone(data) ?? [];
	sorted.sort((a, b) => (a.price > b.price ? 1 : -1));
	const cheapest = sorted?.slice(0, hotWaterHours)


	let count = 0
	let tot = 0;
	let countAllowed = 0
	let totAllowed = 0;
	const graphData = data?.map((row: any) => {
		count++;
		tot += row.price;
		const obj = {
			...row,
			time: parseJSON(row.time).valueOf(), // to unixtimestamp
			allowed: row.level <= maxLevel,
			hotwaterForce: cheapest.some((i) => i.time === row.time),
		}
		if (obj.allowed) {
			countAllowed++;
			totAllowed += row.price;
		}
		return obj;
	}) ?? [];


	const meanPrice = tot / count;
	const meanPriceAllowed = totAllowed / countAllowed;

	// hack we need to att last fake hour
	graphData.push({ time: graphData[graphData.length - 1].time + 3600000 });


	const timeScale = scaleTime()
		.domain([graphData[0].time, graphData[graphData.length - 1].time])
		.nice();


	const xAxisArgs = {
		domain: timeScale.domain().map(date => date.valueOf()),
		scale: timeScale,
		xAxisType: 'number' as const,
		ticks: timeScale.ticks() as any,
		tickFormatter: (time: any) => {
			return format(parseJSON(time), 'HH:mm')
		}
	};


	const savingPercent = meanPriceAllowed / meanPrice;

	if (!kwh) {
		kwh = 0;
	}
	const savingKr = kwh ? Math.round(kwh * (meanPrice - meanPriceAllowed)) : '';

	const refAreaData = graphData.slice(0, -1);

	// TODO how to calculate extra warmwater savings?
	return (
		<div>
			<div>
				<Heading>Beräknad besparing: {Math.round((1 - savingPercent) * 100)}%</Heading>
				<Box pt='10px' fontSize={'18px'} height='80px'>{savingKr != '' ? "Baserat på din årsförbrukning kan du spara upp till " + savingKr + 'kr på ett år.' : ''}</Box>
				<SimpleGrid pt='5' columns={2} spacingX='20px' spacingY='20px'>
					<Box fontSize={'24px'} height='80px'>Ditt dygnsmedelpris: {meanPriceAllowed.toFixed(3)} kr {}</Box>
					<Box fontSize={'24px'} height='80px'>Dygnsmedelpris: {meanPrice.toFixed(3)} kr {}</Box>
				</SimpleGrid>
				<Box pt='10px' height=''><Icon as={InfoIcon} color="green.400" mr={'8px'} />Siffror är beräknade och kan skilja sig beroende på aktuella elpriser och din byggnad</Box>
			</div>
			<div style={{ userSelect: 'none', height: "500px", width: '100%' }}>
				<ResponsiveContainer width="100%" height="100%">
					<ComposedChart data={graphData}>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis
							dataKey="time"
							{...xAxisArgs}
						/>

						<YAxis unit={' kr'} orientation='right' />
						<RechartsTooltip
							labelFormatter={(value: number) => {
								return format((value), 'yyyy-MM-dd HH:mm:ss');
							}}
						/>


						<Line name={"Pris"} key={"price"} dot={false} type="monotone" dataKey={"price"} stroke={"#319795"} />

						{
							refAreaData.map(({ time, allowed }) => (
								<>
									<ReferenceArea x1={time} x2={time + 3600000} shape={<ReferenceBands color={allowed ? '#2aa5a5' : '#A52A2A'} />} >
										{!allowed && <Label angle={-90}>Blockerad </Label>}
									</ReferenceArea>
								</>
							))}
						{
							refAreaData.map(({ time, hotwaterForce }) => {
								if (!hotwaterForce) {
									return null;
								}
								return (
									<>
										<ReferenceArea x1={time} x2={time + 3600000} shape={<ReferenceBands color={'#acd918'} />} >
											<Label angle={-90}>Extra varmvatten </Label>
										</ReferenceArea>
									</>
								)
							})}



					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</div>


	);
}

const ReferenceBands = (props: any) => {
	const { x, height, y, width, color, key } = props;

	return (
		<g>
			<rect
				key={key}
				x={x}
				y={y}
				width={width}
				height={height}
				fill={color}
				fillOpacity={0.3}
			/>
		</g>
	);
}


interface Price {
	time: string;
	currency: string;
	price: number;
	level: number;
}

// interface Prices {
// 	[key: string]: Price;
// }
const graphSourceData: Record<string, Price[]> = {
	"2024-01-08": [{ "time": "2024-01-08T00:00:00+01:00", "currency": "SEK", "price": 0.975, "level": 2 }, { "time": "2024-01-08T01:00:00+01:00", "currency": "SEK", "price": 0.945, "level": 2 }, { "time": "2024-01-08T02:00:00+01:00", "currency": "SEK", "price": 0.917, "level": 1 }, { "time": "2024-01-08T03:00:00+01:00", "currency": "SEK", "price": 0.887, "level": 1 }, { "time": "2024-01-08T04:00:00+01:00", "currency": "SEK", "price": 0.88, "level": 1 }, { "time": "2024-01-08T05:00:00+01:00", "currency": "SEK", "price": 0.92, "level": 1 }, { "time": "2024-01-08T06:00:00+01:00", "currency": "SEK", "price": 1.057, "level": 3 }, { "time": "2024-01-08T07:00:00+01:00", "currency": "SEK", "price": 1.247, "level": 7 }, { "time": "2024-01-08T08:00:00+01:00", "currency": "SEK", "price": 1.358, "level": 8 }, { "time": "2024-01-08T09:00:00+01:00", "currency": "SEK", "price": 1.353, "level": 8 }, { "time": "2024-01-08T10:00:00+01:00", "currency": "SEK", "price": 1.277, "level": 7 }, { "time": "2024-01-08T11:00:00+01:00", "currency": "SEK", "price": 1.183, "level": 6 }, { "time": "2024-01-08T12:00:00+01:00", "currency": "SEK", "price": 1.097, "level": 4 }, { "time": "2024-01-08T13:00:00+01:00", "currency": "SEK", "price": 1.064, "level": 4 }, { "time": "2024-01-08T14:00:00+01:00", "currency": "SEK", "price": 1.149, "level": 5 }, { "time": "2024-01-08T15:00:00+01:00", "currency": "SEK", "price": 1.24, "level": 6 }, { "time": "2024-01-08T16:00:00+01:00", "currency": "SEK", "price": 1.435, "level": 10 }, { "time": "2024-01-08T17:00:00+01:00", "currency": "SEK", "price": 1.483, "level": 10 }, { "time": "2024-01-08T18:00:00+01:00", "currency": "SEK", "price": 1.457, "level": 10 }, { "time": "2024-01-08T19:00:00+01:00", "currency": "SEK", "price": 1.369, "level": 8 }, { "time": "2024-01-08T20:00:00+01:00", "currency": "SEK", "price": 1.28, "level": 7 }, { "time": "2024-01-08T21:00:00+01:00", "currency": "SEK", "price": 1.153, "level": 4 }, { "time": "2024-01-08T22:00:00+01:00", "currency": "SEK", "price": 1.121, "level": 4 }, { "time": "2024-01-08T23:00:00+01:00", "currency": "SEK", "price": 1.026, "level": 2 }],
	"2024-01-09": [{ "time": "2024-01-09T00:00:00+01:00", "currency": "SEK", "price": 0.984, "level": 1 }, { "time": "2024-01-09T01:00:00+01:00", "currency": "SEK", "price": 0.972, "level": 1 }, { "time": "2024-01-09T02:00:00+01:00", "currency": "SEK", "price": 0.953, "level": 1 }, { "time": "2024-01-09T03:00:00+01:00", "currency": "SEK", "price": 0.943, "level": 1 }, { "time": "2024-01-09T04:00:00+01:00", "currency": "SEK", "price": 0.957, "level": 1 }, { "time": "2024-01-09T05:00:00+01:00", "currency": "SEK", "price": 1.001, "level": 1 }, { "time": "2024-01-09T06:00:00+01:00", "currency": "SEK", "price": 1.216, "level": 5 }, { "time": "2024-01-09T07:00:00+01:00", "currency": "SEK", "price": 1.381, "level": 8 }, { "time": "2024-01-09T08:00:00+01:00", "currency": "SEK", "price": 1.494, "level": 10 }, { "time": "2024-01-09T09:00:00+01:00", "currency": "SEK", "price": 1.355, "level": 7 }, { "time": "2024-01-09T10:00:00+01:00", "currency": "SEK", "price": 1.264, "level": 6 }, { "time": "2024-01-09T11:00:00+01:00", "currency": "SEK", "price": 1.181, "level": 4 }, { "time": "2024-01-09T12:00:00+01:00", "currency": "SEK", "price": 1.121, "level": 3 }, { "time": "2024-01-09T13:00:00+01:00", "currency": "SEK", "price": 1.128, "level": 4 }, { "time": "2024-01-09T14:00:00+01:00", "currency": "SEK", "price": 1.259, "level": 6 }, { "time": "2024-01-09T15:00:00+01:00", "currency": "SEK", "price": 1.357, "level": 7 }, { "time": "2024-01-09T16:00:00+01:00", "currency": "SEK", "price": 1.468, "level": 9 }, { "time": "2024-01-09T17:00:00+01:00", "currency": "SEK", "price": 1.546, "level": 10 }, { "time": "2024-01-09T18:00:00+01:00", "currency": "SEK", "price": 1.544, "level": 10 }, { "time": "2024-01-09T19:00:00+01:00", "currency": "SEK", "price": 1.461, "level": 9 }, { "time": "2024-01-09T20:00:00+01:00", "currency": "SEK", "price": 1.316, "level": 6 }, { "time": "2024-01-09T21:00:00+01:00", "currency": "SEK", "price": 1.222, "level": 5 }, { "time": "2024-01-09T22:00:00+01:00", "currency": "SEK", "price": 1.162, "level": 4 }, { "time": "2024-01-09T23:00:00+01:00", "currency": "SEK", "price": 1.062, "level": 2 }],
	"2024-01-10": [{ "time": "2024-01-10T00:00:00+01:00", "currency": "SEK", "price": 0.977, "level": 1 }, { "time": "2024-01-10T01:00:00+01:00", "currency": "SEK", "price": 0.949, "level": 1 }, { "time": "2024-01-10T02:00:00+01:00", "currency": "SEK", "price": 0.949, "level": 1 }, { "time": "2024-01-10T03:00:00+01:00", "currency": "SEK", "price": 0.942, "level": 1 }, { "time": "2024-01-10T04:00:00+01:00", "currency": "SEK", "price": 0.944, "level": 1 }, { "time": "2024-01-10T05:00:00+01:00", "currency": "SEK", "price": 0.998, "level": 1 }, { "time": "2024-01-10T06:00:00+01:00", "currency": "SEK", "price": 1.221, "level": 5 }, { "time": "2024-01-10T07:00:00+01:00", "currency": "SEK", "price": 1.451, "level": 8 }, { "time": "2024-01-10T08:00:00+01:00", "currency": "SEK", "price": 1.603, "level": 10 }, { "time": "2024-01-10T09:00:00+01:00", "currency": "SEK", "price": 1.462, "level": 8 }, { "time": "2024-01-10T10:00:00+01:00", "currency": "SEK", "price": 1.362, "level": 7 }, { "time": "2024-01-10T11:00:00+01:00", "currency": "SEK", "price": 1.224, "level": 7 }, { "time": "2024-01-10T12:00:00+01:00", "currency": "SEK", "price": 1.146, "level": 7 }, { "time": "2024-01-10T13:00:00+01:00", "currency": "SEK", "price": 1.213, "level": 8 }, { "time": "2024-01-10T14:00:00+01:00", "currency": "SEK", "price": 1.297, "level": 8 }, { "time": "2024-01-10T15:00:00+01:00", "currency": "SEK", "price": 1.388, "level": 9 }, { "time": "2024-01-10T16:00:00+01:00", "currency": "SEK", "price": 1.51, "level": 10 }, { "time": "2024-01-10T17:00:00+01:00", "currency": "SEK", "price": 1.612, "level": 10 }, { "time": "2024-01-10T18:00:00+01:00", "currency": "SEK", "price": 1.532, "level": 10 }, { "time": "2024-01-10T19:00:00+01:00", "currency": "SEK", "price": 1.446, "level": 9 }, { "time": "2024-01-10T20:00:00+01:00", "currency": "SEK", "price": 1.313, "level": 8 }, { "time": "2024-01-10T21:00:00+01:00", "currency": "SEK", "price": 1.201, "level": 7 }, { "time": "2024-01-10T22:00:00+01:00", "currency": "SEK", "price": 0.932, "level": 6 }, { "time": "2024-01-10T23:00:00+01:00", "currency": "SEK", "price": 0.337, "level": 1 }],
	"2024-01-11": [{ "time": "2024-01-11T00:00:00+01:00", "currency": "SEK", "price": 0.932, "level": 6 }, { "time": "2024-01-11T01:00:00+01:00", "currency": "SEK", "price": 0.245, "level": 1 }, { "time": "2024-01-11T02:00:00+01:00", "currency": "SEK", "price": 0.273, "level": 1 }, { "time": "2024-01-11T03:00:00+01:00", "currency": "SEK", "price": 0.302, "level": 1 }, { "time": "2024-01-11T04:00:00+01:00", "currency": "SEK", "price": 0.322, "level": 1 }, { "time": "2024-01-11T05:00:00+01:00", "currency": "SEK", "price": 0.385, "level": 1 }, { "time": "2024-01-11T06:00:00+01:00", "currency": "SEK", "price": 1.115, "level": 7 }, { "time": "2024-01-11T07:00:00+01:00", "currency": "SEK", "price": 1.277, "level": 8 }, { "time": "2024-01-11T08:00:00+01:00", "currency": "SEK", "price": 1.541, "level": 10 }, { "time": "2024-01-11T09:00:00+01:00", "currency": "SEK", "price": 1.411, "level": 9 }, { "time": "2024-01-11T10:00:00+01:00", "currency": "SEK", "price": 1.242, "level": 7 }, { "time": "2024-01-11T11:00:00+01:00", "currency": "SEK", "price": 1.164, "level": 7 }, { "time": "2024-01-11T12:00:00+01:00", "currency": "SEK", "price": 1.086, "level": 6 }, { "time": "2024-01-11T13:00:00+01:00", "currency": "SEK", "price": 1.109, "level": 7 }, { "time": "2024-01-11T14:00:00+01:00", "currency": "SEK", "price": 1.187, "level": 7 }, { "time": "2024-01-11T15:00:00+01:00", "currency": "SEK", "price": 1.321, "level": 8 }, { "time": "2024-01-11T16:00:00+01:00", "currency": "SEK", "price": 1.538, "level": 9 }, { "time": "2024-01-11T17:00:00+01:00", "currency": "SEK", "price": 1.681, "level": 10 }, { "time": "2024-01-11T18:00:00+01:00", "currency": "SEK", "price": 1.531, "level": 9 }, { "time": "2024-01-11T19:00:00+01:00", "currency": "SEK", "price": 1.481, "level": 8 }, { "time": "2024-01-11T20:00:00+01:00", "currency": "SEK", "price": 1.332, "level": 6 }, { "time": "2024-01-11T21:00:00+01:00", "currency": "SEK", "price": 1.2, "level": 4 }, { "time": "2024-01-11T22:00:00+01:00", "currency": "SEK", "price": 1.12, "level": 3 }, { "time": "2024-01-11T23:00:00+01:00", "currency": "SEK", "price": 1.086, "level": 3 }],
	"2024-01-12": [{ "time": "2024-01-12T00:00:00+01:00", "currency": "SEK", "price": 1.018, "level": 2 }, { "time": "2024-01-12T01:00:00+01:00", "currency": "SEK", "price": 0.964, "level": 1 }, { "time": "2024-01-12T02:00:00+01:00", "currency": "SEK", "price": 0.931, "level": 1 }, { "time": "2024-01-12T03:00:00+01:00", "currency": "SEK", "price": 0.925, "level": 1 }, { "time": "2024-01-12T04:00:00+01:00", "currency": "SEK", "price": 0.922, "level": 1 }, { "time": "2024-01-12T05:00:00+01:00", "currency": "SEK", "price": 0.932, "level": 1 }, { "time": "2024-01-12T06:00:00+01:00", "currency": "SEK", "price": 1.007, "level": 2 }, { "time": "2024-01-12T07:00:00+01:00", "currency": "SEK", "price": 1.323, "level": 7 }, { "time": "2024-01-12T08:00:00+01:00", "currency": "SEK", "price": 1.576, "level": 10 }, { "time": "2024-01-12T09:00:00+01:00", "currency": "SEK", "price": 1.511, "level": 10 }, { "time": "2024-01-12T10:00:00+01:00", "currency": "SEK", "price": 1.366, "level": 7 }, { "time": "2024-01-12T11:00:00+01:00", "currency": "SEK", "price": 1.254, "level": 6 }, { "time": "2024-01-12T12:00:00+01:00", "currency": "SEK", "price": 1.169, "level": 4 }, { "time": "2024-01-12T13:00:00+01:00", "currency": "SEK", "price": 1.127, "level": 4 }, { "time": "2024-01-12T14:00:00+01:00", "currency": "SEK", "price": 1.142, "level": 4 }, { "time": "2024-01-12T15:00:00+01:00", "currency": "SEK", "price": 1.202, "level": 5 }, { "time": "2024-01-12T16:00:00+01:00", "currency": "SEK", "price": 1.311, "level": 6 }, { "time": "2024-01-12T17:00:00+01:00", "currency": "SEK", "price": 1.428, "level": 8 }, { "time": "2024-01-12T18:00:00+01:00", "currency": "SEK", "price": 1.411, "level": 8 }, { "time": "2024-01-12T19:00:00+01:00", "currency": "SEK", "price": 1.338, "level": 7 }, { "time": "2024-01-12T20:00:00+01:00", "currency": "SEK", "price": 1.189, "level": 4 }, { "time": "2024-01-12T21:00:00+01:00", "currency": "SEK", "price": 1.103, "level": 3 }, { "time": "2024-01-12T22:00:00+01:00", "currency": "SEK", "price": 1.045, "level": 2 }, { "time": "2024-01-12T23:00:00+01:00", "currency": "SEK", "price": 0.953, "level": 1 }],
	"2024-01-16": [{ "time": "2024-01-16T00:00:00+01:00", "currency": "SEK", "price": 0.927, "level": 1 }, { "time": "2024-01-16T01:00:00+01:00", "currency": "SEK", "price": 0.918, "level": 1 }, { "time": "2024-01-16T02:00:00+01:00", "currency": "SEK", "price": 0.903, "level": 1 }, { "time": "2024-01-16T03:00:00+01:00", "currency": "SEK", "price": 0.883, "level": 1 }, { "time": "2024-01-16T04:00:00+01:00", "currency": "SEK", "price": 0.875, "level": 1 }, { "time": "2024-01-16T05:00:00+01:00", "currency": "SEK", "price": 0.941, "level": 1 }, { "time": "2024-01-16T06:00:00+01:00", "currency": "SEK", "price": 1.387, "level": 3 }, { "time": "2024-01-16T07:00:00+01:00", "currency": "SEK", "price": 2.181, "level": 6 }, { "time": "2024-01-16T08:00:00+01:00", "currency": "SEK", "price": 3.108, "level": 10 }, { "time": "2024-01-16T09:00:00+01:00", "currency": "SEK", "price": 3.051, "level": 10 }, { "time": "2024-01-16T10:00:00+01:00", "currency": "SEK", "price": 2.819, "level": 9 }, { "time": "2024-01-16T11:00:00+01:00", "currency": "SEK", "price": 2.262, "level": 7 }, { "time": "2024-01-16T12:00:00+01:00", "currency": "SEK", "price": 1.918, "level": 5 }, { "time": "2024-01-16T13:00:00+01:00", "currency": "SEK", "price": 1.601, "level": 4 }, { "time": "2024-01-16T14:00:00+01:00", "currency": "SEK", "price": 1.619, "level": 4 }, { "time": "2024-01-16T15:00:00+01:00", "currency": "SEK", "price": 1.761, "level": 4 }, { "time": "2024-01-16T16:00:00+01:00", "currency": "SEK", "price": 1.918, "level": 5 }, { "time": "2024-01-16T17:00:00+01:00", "currency": "SEK", "price": 1.926, "level": 5 }, { "time": "2024-01-16T18:00:00+01:00", "currency": "SEK", "price": 1.501, "level": 3 }, { "time": "2024-01-16T19:00:00+01:00", "currency": "SEK", "price": 1.261, "level": 2 }, { "time": "2024-01-16T20:00:00+01:00", "currency": "SEK", "price": 1.077, "level": 1 }, { "time": "2024-01-16T21:00:00+01:00", "currency": "SEK", "price": 0.972, "level": 1 }, { "time": "2024-01-16T22:00:00+01:00", "currency": "SEK", "price": 0.925, "level": 1 }, { "time": "2024-01-16T23:00:00+01:00", "currency": "SEK", "price": 0.876, "level": 1 }],
};
